import React, { useMemo } from 'react';
import { styled } from '@material-ui/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-regular-svg-icons';

import { useQueryParams } from '../../../hooks/router';
import { CourseGraphQL, getNextLessonCta } from '../../../api/content';
import { useUser } from '../User';
import TopicDetail from './TopicDetail';
import { FullWidthMobileButton } from '../Buttons';
import ClassroomProgressChart from './ClassroomProgressChart';
import './index.css';

import { theme as picardyTheme } from '@picardy/picardy-design-system';

const NextLessonButton = styled(FullWidthMobileButton)(
  ({ theme }: { theme: typeof picardyTheme }) => ({
    [theme.breakpoints.up('lg')]: {
      width: '100%',
    },
  }),
);

export interface Props {
  course: CourseGraphQL;
  hideEducatorFeatures?: boolean;
}

export default function CourseDetail({ course, hideEducatorFeatures }: Props) {
  const user = useUser();
  const params = useQueryParams();
  const isAdmin = useMemo(() => user?.roles?.superuser, [user]);
  const isEducator = useMemo(() => user?.roles?.teacher, [user]);
  const showEducatorFeatures = isEducator && !hideEducatorFeatures;

  return (
    <>
      <Paper elevation={2} style={{ overflow: 'hidden', padding: '2em' }}>
        <Grid container style={{ maxWidth: 'calc(100vw - 2em)' }} spacing={3}>
          <Grid item lg={4} md={12} xs={12}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="flex-start"
              marginBottom={1}
            >
              <Typography variant="h3">{course.title}</Typography>

              {isAdmin && (
                <Link
                  variant="h5"
                  href={course.adminUrl}
                  aria-label="Edit course in Picardy Admin"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Link>
              )}
            </Box>
            <Typography
              variant="body1"
              style={{ marginBottom: '1em' }}
              className="course-subtitle"
            >
              <div dangerouslySetInnerHTML={{ __html: course.subtitle }} />
            </Typography>

            <NextLessonButton
              style={{ marginBottom: '1em' }}
              variant="contained"
              size="large"
              type="submit"
              color="primary"
              href={course.nextLesson ? course.nextLesson.url : course.url}
            >
              {getNextLessonCta(course)}
            </NextLessonButton>

            {showEducatorFeatures && params.has('classroom') && (
              <ClassroomProgressChart
                classroomId={params.get('classroom')!}
                courseId={course._id}
              />
            )}
          </Grid>
          <Grid item lg={8} md={12} xs={12} style={{ marginBottom: '-2em' }}>
            {course.topicsEntities.map(topic => (
              <TopicDetail
                key={topic._id}
                topic={topic}
                hideEducatorFeatures={hideEducatorFeatures}
              />
            ))}
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
